import DnsIcon from "@mui/icons-material/Dns";
import {
    Box,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Grid,
    TextField,
    Alert,
} from "@mui/material";
import { Localized } from "@fluent/react";
import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { AppRequiredMessage } from "../../../app/AppRequiredMessage";
import { showError } from "../../notifications/notificationsSlice";
import { postWithAuth } from "../../../http";
import { CheckSerialNoResponse, checkSerialNo, ComputerizedSystem } from "../computerizedSystemsApi";
import { GampCategory } from "../gampDictionary";
import { GampCategorySelect } from "../components/GampCategorySelect";
import { StructureTreeSelect } from "../../structure/StructureTreeSelect";
import { AppId } from "../../../app/appTypes";
import { CancelButton } from "../../../app/components/CancelButton";
import { SaveButton } from "../../../app/components/SaveButton";
import { selectAllStructureNodes } from "../../device/structureSlice";
import { SuppliersSelector } from "../../suppliers/components/SuppliersSelector";
import { StatusLevelAlert } from "../../../app/components/StatusLevelAlert";
import { Help } from "../../dialogs/HelpDialog";

export interface CreateComputerizedSystemProps {
    supplierId?: string;
    onSuccess: (system: ComputerizedSystem) => void;
    onCancel: () => void;
}

export const CreateComputerizedSystem = (props: CreateComputerizedSystemProps) => {
    const { onSuccess, onCancel } = props;
    const dispatch = useAppDispatch();
    const rootDeviceId = useAppSelector(state => selectAllStructureNodes(state).find(s => s.code === 'COMP SYS')?.id)

    const [name, setName] = useState("");
    const [identifier, setIdentifier] = useState("");
    const [softwareVersion, setSoftwareVersion] = useState("");
    const [vendor, setVendor] = useState("");
    const [supplier, setSupplier] = useState(props.supplierId ?? "");
    const [structureId, setStructureId] = useState<AppId | undefined>();
    const [gampCategory, setGampCategory] = useState<GampCategory>(GampCategory.Category4_Configured);
    const [isGxpRelevant, setIsGxpRelevant] = useState<boolean>(false);
    const [description, setDescription] = useState("");

    const [validationErrors, setValidationErrors] = useState(false);
    const [saving, setSaving] = useState(false);
    const [identifierValidation, setIdentifierValidation] = useState<CheckSerialNoResponse | null>(null);
    const [identifierValidationBusy, setIdentifierValidationBusy] = useState(false);
    const identifierValidationRequestRef = useRef(0);
    const identifierValidationHideBusyTimeoutRef = useRef<number | null>(null);

    // handlers
    const handleNameChange = useMemo(
        () => (e: ChangeEvent<HTMLInputElement>) => setName(e.target.value),
        []
    );
    const handleSoftwareVersionChange = useMemo(
        () => (e: ChangeEvent<HTMLInputElement>) => setSoftwareVersion(e.target.value),
        []
    );
    const handleVendorChange = useMemo(
        () => (e: ChangeEvent<HTMLInputElement>) => setVendor(e.target.value),
        []
    );
    const handleDescriptionChange = useMemo(
        () => (e: ChangeEvent<HTMLInputElement>) => setDescription(e.target.value),
        []
    );
    const handleStructureChanged = useCallback((id: AppId | undefined) => setStructureId(id), []);

    useEffect(() => {
        if (identifierValidationHideBusyTimeoutRef.current !== null) {
            window.clearTimeout(identifierValidationHideBusyTimeoutRef.current);
            identifierValidationHideBusyTimeoutRef.current = null;
        }

        const normalizedIdentifier = identifier.trim();
        const normalizedSupplierId = Number(supplier);
        const normalizedStructureId = Number(structureId);

        if (!normalizedIdentifier || !Number.isInteger(normalizedStructureId) || normalizedStructureId <= 0 || !Number.isInteger(normalizedSupplierId) || normalizedSupplierId <= 0) {
            setIdentifierValidation(null);
            setIdentifierValidationBusy(false);
            return;
        }

        const requestId = identifierValidationRequestRef.current + 1;
        identifierValidationRequestRef.current = requestId;

        const timeoutId = window.setTimeout(async () => {
            const busyShownAt = Date.now();
            setIdentifierValidationBusy(true);

            const result = await dispatch(checkSerialNo({
                computerizedSystemIdentifier: normalizedIdentifier,
                structureId: normalizedStructureId,
                supplierId: normalizedSupplierId,
            }));

            if (identifierValidationRequestRef.current !== requestId) {
                return;
            }

            if (checkSerialNo.fulfilled.match(result)) {
                setIdentifierValidation(result.payload as CheckSerialNoResponse);
            } else {
                setIdentifierValidation({ status: "SERVER_ERROR", level: 4 });
            }

            const elapsed = Date.now() - busyShownAt;
            const remaining = 350 - elapsed;

            if (remaining > 0) {
                identifierValidationHideBusyTimeoutRef.current = window.setTimeout(() => {
                    if (identifierValidationRequestRef.current !== requestId) {
                        return;
                    }
                    setIdentifierValidationBusy(false);
                }, remaining);
            } else {
                setIdentifierValidationBusy(false);
            }
        }, 500);

        return () => {
            window.clearTimeout(timeoutId);
            if (identifierValidationHideBusyTimeoutRef.current !== null) {
                window.clearTimeout(identifierValidationHideBusyTimeoutRef.current);
                identifierValidationHideBusyTimeoutRef.current = null;
            }
        };
    }, [dispatch, identifier, structureId, supplier]);

    // validation flags
    const nameError = validationErrors && name.trim() === "";
    const softwareVersionError = validationErrors && softwareVersion.trim() === "";
    const vendorError = validationErrors && vendor.trim() === "";
    const gampError = validationErrors && !gampCategory;

    const normalizedSupplierId = useMemo(() => {
        const normalized = String(supplier ?? "").trim()
        return normalized !== "" && normalized !== "0" ? normalized : null
    }, [supplier])

    const handleSaveClick = async () => {
        setValidationErrors(true);

        const ok =
            name.trim() !== "" &&
            identifier.trim() !== "" &&
            softwareVersion.trim() !== "" &&
            vendor.trim() !== "" &&
            !!structureId &&
            !!gampCategory;

        if (!ok) return;

        setSaving(true);
        try {
            const payload: Omit<ComputerizedSystem, "id"> = {
                name: name.trim(),
                computerizedSystemIdentifier: identifier.trim(),
                description: description.trim() || null,
                structureId: structureId!,
                itOwnerId: null,
                department: null,
                status: null,
                isGxpRelevant: isGxpRelevant,
                criticality: null,
                validationStatus: null,
                validationDate: null,
                nextReviewDate: null,
                softwareVersion: softwareVersion.trim(),
                vendor: vendor.trim(),
                supplierId: normalizedSupplierId,
                supplierName: null,
                backupDescription: null,
                gampCategory: gampCategory,
                createDate: new Date(),
                createdBy: "",
                createdByFullName: ""
            };

            const result = await dispatch(
                postWithAuth({
                    url: "api/computerized-systems",
                    payload,
                })
            );

            if (postWithAuth.fulfilled.match(result)) {
                const created = result.payload as ComputerizedSystem;
                onSuccess(created);
            } else {
                let errorMsg = "Błąd zapisu";
                const p: any = (result as any).payload;
                if (p?.kind === "http" && p?.problem?.title) errorMsg = p.problem.title;
                dispatch(showError(errorMsg));
            }
        } catch {
            dispatch(showError("Błąd zapisu"));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog maxWidth="md" fullWidth open onClose={onCancel}>
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    bgcolor: "grey.100",
                    borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                }}
            >
                <DnsIcon fontSize="small" color="success" />
                <Localized id="cs-create-title">Nowy system skomputeryzowany</Localized>
            </DialogTitle>

            <DialogContent>
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid item xs={12}>
                        <StructureTreeSelect
                            defaultStructureId={structureId}
                            onStructureSelected={handleStructureChanged}
                            rootId={rootDeviceId}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <SuppliersSelector
                            selectedStructureId={structureId ?? null}
                            showFiltersTab
                            filterByStructure={false}
                            value={supplier}
                            onSubmit={(supplier) => setSupplier(String(supplier?.supplierId ?? "").trim())}
                            disabled={!!props.supplierId}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            required
                            fullWidth
                            value={identifier}
                            inputProps={{ maxLength: 200 }}
                            onChange={(e) => setIdentifier(e.target.value)}
                            label={<Localized id="cs-identifier">Numer identyfikacyjny</Localized>}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            required
                            fullWidth
                            error={nameError}
                            helperText={nameError && <AppRequiredMessage />}
                            value={name}
                            inputProps={{ maxLength: 200 }}
                            onChange={handleNameChange}
                            label={<Localized id="cs-name">Nazwa</Localized>}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            value={description}
                            inputProps={{ maxLength: 4000 }}
                            onChange={handleDescriptionChange}
                            label={<Localized id="cs-description">Opis (opcjonalnie)</Localized>}
                            multiline
                            minRows={2}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            required
                            fullWidth
                            error={softwareVersionError}
                            helperText={softwareVersionError && <AppRequiredMessage />}
                            value={softwareVersion}
                            inputProps={{ maxLength: 100 }}
                            onChange={handleSoftwareVersionChange}
                            label={<Localized id="cs-software-version">Software version</Localized>}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            required
                            fullWidth
                            error={vendorError}
                            helperText={vendorError && <AppRequiredMessage />}
                            value={vendor}
                            inputProps={{ maxLength: 200 }}
                            onChange={handleVendorChange}
                            label={<Localized id="cs-vendor">Vendor</Localized>}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Box
                            sx={{
                                p: 1.5,
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 2,
                                bgcolor: "grey.50",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                            }}
                        >
                            <FormControlLabel
                                sx={{ m: 0 }}
                                control={
                                    <Checkbox
                                        checked={isGxpRelevant}
                                        onChange={(e) => setIsGxpRelevant(e.target.checked)}
                                    />
                                }
                                label={
                                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                                        <Localized id="cs-is-gxp">
                                            <span>System GxP</span>
                                        </Localized>
                                    </span>
                                }
                            />

                            <Help id="71f8633a-6475-4d19-b674-f7bc2fc391ec"/>
                        </Box>
                    </Grid>

                    <Grid item xs={12} md={12}>
                        <GampCategorySelect
                            value={gampCategory}
                            onChange={setGampCategory}
                            error={gampError}
                            helperText={<AppRequiredMessage />}
                        />
                    </Grid>
                    {(identifierValidationBusy || !!identifierValidation) && (
                        <Grid item xs={12}>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                {identifierValidationBusy ? (
                                    <Alert severity="info" sx={{ m: 0, width: "100%" }}>Sprawdzanie numeru identyfikacyjnego...</Alert>
                                ) : identifierValidation ? (
                                    <StatusLevelAlert type="CS" status={identifierValidation.status} level={identifierValidation.level} sx={{ m: 0, width: "100%" }} />
                                ) : null}
                            </Box>
                        </Grid>
                    )}
                </Grid>
            </DialogContent>

            <DialogActions
                sx={{ bgcolor: "background.paper", borderTop: (t) => `1px solid ${t.palette.divider}` }}
            >
                <SaveButton loading={saving} onClick={handleSaveClick}></SaveButton>
                <CancelButton loading={saving} onClick={onCancel}></CancelButton>
            </DialogActions>
        </Dialog>
    );
};
